import { guid } from '@progress/kendo-react-common';
import { Window, WindowActionsEvent, WindowMoveEvent } from '@progress/kendo-react-dialogs';
import { windowStage } from '@progress/kendo-react-dialogs/dist/npm/StageEnum';
import React, { createContext, FC, useContext, useReducer, useCallback, useMemo } from 'react';
import { sortNumbersAscending } from '../../utils/constants';

interface IContext {
    openWindow: (payload: IWindowProps) => void;
}

export interface IState {
    windowDialog: IWindow[];
}

interface IWindowLocation {
    top: number;
    left?: number;
}

interface IWindowSize {
    width: number;
    height: number;
    minimizedWidth: number;
}

interface IWindowState {
    location?: Partial<IWindowLocation>;
    size: IWindowSize;
}

interface IWindowComponentAction {
    onClose: () => void;
}

export interface IWindowComponentProps<T> extends IWindowComponentAction {
    componentProps: T;
}

/**
 * TODO:
 * openInMiddle?: boolean | undefined
 */
interface IWindowProps {
    title: string;
    component: FC<IWindowComponentProps<any>>;
    isDialogBehaviour?: boolean | undefined;
    width?: number | undefined;
    height?: number | string | undefined;
    center?: boolean;
    onBeforeClose?: () => void;
}

interface IWindow extends IWindowProps {
    id: string;
    minimizedOrder: number | null;
    state: IWindowState;
    stage: windowStage;
}

const except = (value: number, index: number, self: number[]) => {
    return self.filter(x => x === value).length > 1 ? false : true;
};

const getOrder = (minimizedOrder: number | null, window: IWindow[]) => {
    let order = 0;
    if (minimizedOrder == null) {
        const windowOrderFiltered = window
            .filter(x => x.minimizedOrder != null)
            .map(x => x.minimizedOrder!)
            .sort(sortNumbersAscending);
        const possibleOrder = [...window.map((x, i) => i + 1), ...windowOrderFiltered].filter(except);
        order = possibleOrder.length > 0 ? possibleOrder[0] : 0;
    }
    return order;
};

const initialLocation: IWindowLocation = { top: 100, left: undefined };
const initialSize: IWindowSize = { width: 600, height: 600, minimizedWidth: 200 };
const initialState: IState = { windowDialog: [] };

const OPEN_WINDOW = 'shell/window/OPEN_WINDOW';
const SET_STAGE = 'shell/window/SET_STAGE';
const CLOSE_WINDOW = 'shell/window/CLOSE_WINDOW';
const MOVE_WINDOW = 'shell/window/MOVE_WINDOW';
const RESIZE_WINDOW = 'shell/window/RESIZE_WINDOW';

export const actions = {
    openWindow: (payload: IWindow) => ({
        type: OPEN_WINDOW as typeof OPEN_WINDOW,
        payload
    }),
    setStage: (stage: windowStage, order: number | null, id: string) => ({
        type: SET_STAGE as typeof SET_STAGE,
        payload: { stage, order, id }
    }),
    closeWindow: (payload: string) => ({
        type: CLOSE_WINDOW as typeof CLOSE_WINDOW,
        payload
    }),
    moveWindow: (move: Partial <IWindowLocation>, id: string) => ({
        type: MOVE_WINDOW as typeof MOVE_WINDOW,
        payload: { move, id }
    }),
    resizeWindow: (resize: IWindowSize, id: string) => ({
        type: RESIZE_WINDOW as typeof RESIZE_WINDOW,
        payload: { resize, id }
    })
};

type ActionTypes = ActionUnion<typeof actions>;

const reducer = (state: IState, action: ActionTypes): IState => {
    switch (action.type) {
        case OPEN_WINDOW:
            return {
                ...state,
                windowDialog: [...state.windowDialog, action.payload]
            };
        case SET_STAGE:
            return {
                ...state,
                windowDialog: state.windowDialog.map(x => {
                    if (x.id === action.payload.id) {
                        x.minimizedOrder = action.payload.order;
                        x.stage = action.payload.stage;
                    }
                    return x;
                })
            };
        case CLOSE_WINDOW:
            return {
                ...state,
                windowDialog: state.windowDialog.filter(x => x.id !== action.payload)
            };
        case MOVE_WINDOW:
            return {
                ...state,
                windowDialog: state.windowDialog.map(x => {
                    if (x.id === action.payload.id) {
                        x.state = { ...x.state, location: action.payload.move };
                    }
                    return x;
                })
            };
        case RESIZE_WINDOW:
            return {
                ...state,
                windowDialog: state.windowDialog.map(x => {
                    if (x.id === action.payload.id) {
                        x.state = { ...x.state, size: action.payload.resize };
                    }
                    return x;
                })
            };
        default:
            return state;
    }
};

const WindowContext = createContext<IContext>({} as IContext);

const WindowContextProvider: FC = props => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const openWindow = useCallback((window: IWindowProps) => {
        const id = guid();
        dispatch({
            type: OPEN_WINDOW,
            payload: {
                ...window,
                id,
                minimizedOrder: null,
                state: { location: window.center === false ? initialLocation : undefined, size: initialSize },
                stage: windowStage.DEFAULT
            }
        });
    }, []);

    const closeWindowById = (id: string) => {
        state.windowDialog.find(x=> x.id === id)?.onBeforeClose?.();
        dispatch({ type: CLOSE_WINDOW, payload: id });
    };

    const handleMove = (event: WindowMoveEvent) => {
        const props = event.target.props as any;
        const id = props['id'] as string;
        dispatch({
            type: MOVE_WINDOW,
            payload: { move: { top: event.target.state.top, left: event.target.state.left }, id }
        });
    };

    const handleStageChange = (event: WindowActionsEvent) => {
        const props = event.target.props as any;
        const id = props['id'] as string;

        const windowDialog = state.windowDialog.find(x => x.id === id);
        if (windowDialog) {
            if (event.state === windowStage.MINIMIZED) {
                const order = getOrder(windowDialog.minimizedOrder, state.windowDialog);
                dispatch({ type: SET_STAGE, payload: { stage: windowStage.MINIMIZED, order, id } });
                dispatch({ 
                    type: RESIZE_WINDOW, 
                    payload: { resize: { ...windowDialog.state.size, width: initialSize.minimizedWidth }, id }
                });
                dispatch({
                    type: MOVE_WINDOW,
                    payload: { 
                        move: { 
                            top: window.innerHeight - 55, 
                            left: window.innerWidth - initialSize.minimizedWidth * order 
                        }, 
                        id 
                    }
                });
            }
            if (event.state === windowStage.DEFAULT) {
                dispatch({ type: SET_STAGE, payload: { stage: windowStage.DEFAULT, order: null, id } });
                dispatch({ type: RESIZE_WINDOW, payload: { resize: { ...windowDialog.state.size, width: initialSize.width }, id } });
                dispatch({
                    type: MOVE_WINDOW,
                    payload: { move: { ...windowDialog.state.location}, id }
                });
            }
            if (event.state === windowStage.FULLSCREEN) {
                dispatch({ type: SET_STAGE, payload: { stage: windowStage.FULLSCREEN, order: null, id } });
            }
        }
    };

    const value = useMemo(() => ({ openWindow }), [openWindow]);

    return (
        <WindowContext.Provider value={value}>
            {state.windowDialog.map((w, index) => (
                <Window
                    key={index}
                    {...{ id: w.id }}
                    height={w.height ? (typeof w.height === 'number' ? w.height : undefined) : w.state.size.height}
                    left={w.state.location?.left}
                    modal={w.isDialogBehaviour ? w.isDialogBehaviour : false}
                    resizable={false}
                    title={w.title}
                    top={w.state.location?.top}
                    width={w.stage === windowStage.MINIMIZED ? w.state.size.minimizedWidth : w.width ? w.width : w.state.size.width}
                    onClose={() => closeWindowById(w.id)}
                    onMove={handleMove}
                    onStageChange={handleStageChange}
                    style={{ height: typeof w.height === 'string' ? w.height : undefined }}
                >
                    <w.component
                        componentProps={w.component.defaultProps && w.component.defaultProps.componentProps}
                        onClose={() => closeWindowById(w.id)}
                    />
                </Window>
            ))}
            {props.children}
        </WindowContext.Provider>
    );
};

const useWindow = () => useContext(WindowContext);

export { WindowContextProvider, WindowContext, useWindow };
